import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import {
  listInstitutions,
  type Institution,
  type ListInstitutionsParams,
} from "@/api/institutions";

export default function InstitutionsLab() {
  const [filters, setFilters] = useState<ListInstitutionsParams>({
    limit: 24,
    sort: "follower_count",
    order: "desc",
  });
  const [query, setQuery] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["institutions", filters, query],
    queryFn: () =>
      listInstitutions({ ...filters, q: query || undefined }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Institutions"
        subtitle="Sacred-worship institutions (temples, gurudwaras, mosques, churches). Browse, follow, register."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Institutions" },
        ]}
        actions={
          <>
            <Link to="/lab/institutions/followed">
              <Button variant="secondary" size="sm">
                Followed
              </Button>
            </Link>
            <Link to="/lab/institutions/new">
              <Button size="sm">Register new</Button>
            </Link>
          </>
        }
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <InputField
            label="Search"
            placeholder="Name or description"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SelectField
            label="Type"
            value={filters.type ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, type: e.target.value || undefined })
            }
            options={[
              { value: "", label: "All" },
              { value: "temple", label: "Temple" },
              { value: "gurudwara", label: "Gurudwara" },
              { value: "mosque", label: "Mosque" },
              { value: "church", label: "Church" },
              { value: "community_center", label: "Community center" },
            ]}
          />
          <InputField
            label="City"
            value={filters.city ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, city: e.target.value || undefined })
            }
          />
          <InputField
            label="State"
            value={filters.state ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, state: e.target.value || undefined })
            }
          />
        </div>
      </section>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No institutions match.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.data.items.map((i) => (
            <InstitutionCard key={i.id} i={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function InstitutionCard({ i }: { i: Institution }) {
  return (
    <Link
      to={`/lab/institutions/${i.slug}`}
      className="block bg-white border border-gray-200 rounded-2xl p-4 hover:border-primary-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-3">
        {i.avatarUrl ? (
          <img
            src={i.avatarUrl}
            alt=""
            className="w-12 h-12 rounded-lg object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center text-xl">
            🛕
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{i.name}</h3>
            {i.verificationStatus === "approved" && (
              <Badge variant="green">verified</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {i.institutionType} · {i.city}, {i.state}
          </p>
          <div className="flex gap-3 mt-2 text-xs text-gray-600">
            <span>{i.followerCount} followers</span>
            <span>{i.eventCount} events</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
